import React, { FC, ReactNode, useEffect, useMemo, useRef, useState } from "react";
import * as Enums from "@/utils/enums";
import { IconTrash } from "@tabler/icons";
import SectionTable from "@/PageComponents/SectionTable/SectionTable";
import { IconPlus } from "@tabler/icons-react";
import { Anchor, Box, Button, Flex, Menu, Text, useMantineTheme } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { SimpleColumnMapping } from "@/PageComponents/SimpleTable/SimpleTable";
import featureService from "@/services/feature/feature-service";
import constants from "@/utils/constants";
import helper from "@/utils/helper";
import SCModal from "@/PageComponents/Modal/SCModal";
import ImageWithZoom from "@/PageComponents/Attachment/ImageWithZoom";
import permissionService from "@/services/permission/permission-service";
import { calculateLineTotalInclusive, calculateInclusive } from '@/utils/line-totals';
import { useLineItems } from "@/hooks/useLineItems";
import { useStableCallback } from "@/hooks/useStableCallback";
import WarehouseTypeIcon from "@/PageComponents/Warehouse/WarehouseTypeIcon";

const InvoiceSectionTable: FC<
    {
        descriptionColumnWidth?: string
        invoice: any;
        invoiceItems: any[];
        itemId: string;
        module: number;
        onDataUpdate: (newData: any[]/*, newSections: any[]*/) => void;
        addOptions: any[];
        onAddAction: (name: string) => void;
        onAddToSectionAction: (name: string, group: any) => void
        integration: boolean;
        companyTaxPercentage: number;
        onItemClicked: (item: any, index: number) => void
        onPredictedDefaultSectionPdfSettingsChanged?: (pdfSettings: { HideLineItems: boolean; DisplaySubtotal: boolean; }) => void
        customChildren?: ReactNode
        onColumnMappingLoaded?: (mapping: SimpleColumnMapping[]) => void
        userColumnConfig?: any[]
    }> = (props: any) => {

        const theme = useMantineTheme();
        const { data: hasStockControl } = useQuery(['hasStockControl'], () => featureService.getFeature(constants.features.STOCK_CONTROL));

        const [showImage, setShowImage] = useState<any>();

        const canUpdate = useMemo(() => props.invoice.InvoiceStatus === Enums.InvoiceStatus.Draft, [props.invoice.InvoiceStatus])

        const { items: currentData, savingItem, updateField, commit, removeItem, removeSection } = useLineItems(
            props.invoiceItems,
            (items: any[]) => props.onDataUpdate(items),
            { costing: false }
        )

        const hasThumbnails = useMemo(() => currentData.some((x: any) => x.Inventory?.ThumbnailUrl), [currentData])

        const handleItemAction = useStableCallback((actionName: string, item: any, itemIndex: number, group: any) => {
            if (actionName === 'code') {
                props.onItemClicked(item, itemIndex)
            } else if (actionName === 'delete') {
                removeItem(item.ID)
            }
        })
        const handleSectionAction = useStableCallback((name: any, group: any) => {
            if (name === 'delete') {
                removeSection(group.id)
            } else {
                props.onAddToSectionAction(name, group)
            }
        })

        const [hasManageCostingPermission] = useState(permissionService.hasPermission(Enums.PermissionName.ManageCosting));

        const mapping = useMemo(() => {
            return [
                ...(hasThumbnails ? [{
                    key: 'ImageUrl',
                    label: '',
                    valueFunction: (item: any) => item.Inventory?.ThumbnailUrl && <img
                        src={item.Inventory?.ThumbnailUrl}
                        style={{ height: 36, width: 36, borderRadius: 36, cursor: "pointer" }}
                        onClick={() => setShowImage(item)}
                    />
                }] : []),
                {
                    key: 'InventoryCode',
                    label: 'Code',
                    valueFunction: (item: any) => item.ProductID ? item.ProductNumber : item.InventoryCode ?? (canUpdate ? 'Edit' : ''),
                    linkAction: 'code'
                },
                {
                    key: 'Description',
                    keyFunction: (item: any) => item.InventoryID ? 'Description' : 'Description',
                    label: 'Description',
                    // type: canUpdate ? 'textInput' : undefined,
                    typeFunction: (item: any) => item.InventoryID ? 'textInput' : 'textArea',
                    stylingProps: {
                        miw: props.descriptionColumnWidth ?? '30vw',
                        darkerText: true
                    }
                },
                {
                    key: 'InvoiceItemType',
                    label: 'Status',
                    type: 'status',
                    colorFunction: (item: any) => (item.InvoiceItemType === Enums.InvoiceItemType.Inventory ? (item.Integrated ? 'Green' : item.IntegrationMessage ? `Yellow` : undefined) : undefined),
                    valueFunction: (item: any) => (
                        item.InvoiceItemType === Enums.InvoiceItemType.Inventory && (item.Integrated ? 'Synced' : item.IntegrationMessage ? 'Error' : 'Not Synced'
                        ) || null),
                    showFunction: (item: any) => (item.InvoiceItemType === Enums.InvoiceItemType.Inventory),
                },
                {
                    key: 'Quantity',
                    label: 'Quantity',
                    typeFunction: (item: any) => item.InvoiceItemType === Enums.InvoiceItemType.Inventory ? (canUpdate ? 'numberInput' : undefined) : undefined,
                    min: 0,
                    customNumberProps: {
                        focusOnSelect: true
                    },
                    alignRight: true,
                    showFunction: (item: any) => (item.InvoiceItemType === Enums.InvoiceItemType.Inventory)
                },
                ...(hasStockControl && [{
                    key: 'Warehouse',
                    label: 'Warehouse',
                    alignRight: false,
                    valueFunction: (item: any) => {
                        return helper.isInventoryWarehoused(item.Inventory) ?
                            <Flex align={"center"} gap={'xs'}>
                                <WarehouseTypeIcon warehouse={item.Warehouse} />
                                <span style={item.Warehouse ? {} : { color: theme.colors.yellow[6], fontWeight: "bold" }} title={item.Warehouse?.Name}>{item.Warehouse?.Code ?? "Unallocated"}</span>
                            </Flex>
                            : <></>;
                    }
                }] || []),
                {
                    key: 'UnitPriceExclusive',
                    label: 'Price Excl',
                    alignRight: true,
                    typeFunction: (item: any) => item.InvoiceItemType === Enums.InvoiceItemType.Inventory ? (canUpdate ? 'numberInput' : undefined) : undefined,
                    // min: 0,
                    currencyValue: true,
                    customNumberProps: {
                        focusOnSelect: true
                    },
                    showFunction: (item: any) => (item.InvoiceItemType === Enums.InvoiceItemType.Inventory),
                    columConfigOptions: {
                        allowShowToggle: true,
                        defaultShown: true,
                        disabled: true,
                    }
                },
                {
                    key: 'UnitPriceInclusive',
                    label: 'Price Incl',
                    alignRight: true,
                    typeFunction: (item: any) => item.InvoiceItemType === Enums.InvoiceItemType.Inventory ? (canUpdate ? 'numberInput' : undefined) : undefined,
                    currencyValue: true,
                    customNumberProps: {
                        focusOnSelect: true
                    },
                    showFunction: (item: any) => (item.InvoiceItemType === Enums.InvoiceItemType.Inventory),
                    valueFunction: (item: any) => {
                        // Calculate UnitPriceInclusive from UnitPriceExclusive and TaxPercentage
                        return calculateInclusive(item.UnitPriceExclusive, item.TaxPercentage);
                    },
                    columConfigOptions: {
                        allowShowToggle: true,
                        disabled: true,
                        defaultShown: false,
                    }
                },
                {
                    key: 'LineDiscountPercentage',
                    label: 'Discount %',
                    alignRight: true,
                    typeFunction: (item: any) => item.InvoiceItemType === Enums.InvoiceItemType.Inventory ? (canUpdate ? 'numberInput' : undefined) : undefined,
                    customNumberProps: {
                        focusOnSelect: true
                    },
                    showFunction: (item: any) => (item.InvoiceItemType === Enums.InvoiceItemType.Inventory),
                    numberInputProps: {
                        min: 0,
                        max: 100
                    },
                    columConfigOptions: {
                        allowShowToggle: true,
                    }
                },
                {
                    key: 'TaxPercentage',
                    label: 'Tax Rate',
                    alignRight: true,
                    typeFunction: (item: any) => item.InvoiceItemType === Enums.InvoiceItemType.Inventory ? (canUpdate ? 'selectInput' : undefined) : undefined,
                    showFunction: (item: any) => (item.InvoiceItemType === Enums.InvoiceItemType.Inventory),
                    selectOptions: [
                        {
                            label: 'Standard Rate',
                            value: props.companyTaxPercentage + ''
                        },
                        {
                            label: props.companyTaxPercentage === 0 ? 'Default (No VAT)' : 'No VAT',
                            value: 0 + ''
                        },
                    ].filter((x: any) => x.label !== 'Standard Rate' || props.companyTaxPercentage !== 0),
                    columConfigOptions: {
                        allowShowToggle: true,
                    }
                },
                {
                    key: 'LineTotalExclusive',
                    label: 'Total Excl',
                    currencyValue: true,
                    alignRight: true,
                    // type: canUpdate ? 'numberInput' : undefined,
                    min: 0,
                    columConfigOptions: {
                        allowShowToggle: true,
                        defaultShown: true,
                        disabled: true,
                    },
                    showFunction: (item: any) => (item.InvoiceItemType === Enums.InvoiceItemType.Inventory),
                },
                {
                    key: 'LineTotalInclusive',
                    label: 'Total Incl',
                    currencyValue: true,
                    alignRight: true,
                    min: 0,
                    showFunction: (item: any) => (item.InvoiceItemType === Enums.InvoiceItemType.Inventory),
                    valueFunction: (item: any) => {
                        if (!item || typeof item.LineTotalExclusive === 'undefined' || typeof item.TaxPercentage === 'undefined') {
                            return 0;
                        }

                    // Calculated from the unit price, not from the rounded exclusive line total -
                    // rebuilding it from that turns an entered R80.00 into R80.01.
                    return calculateLineTotalInclusive({
                        quantity: item.Quantity,
                        unitPrice: item.UnitPriceExclusive,
                        discountPercentage: item.LineDiscountPercentage,
                        taxPercentage: item.TaxPercentage,
                    });
                    },
                    columConfigOptions: {
                        allowShowToggle: true,
                        defaultShown: false
                    }
                }
            ].filter((x: any) => x.key === 'InvoiceItemType' ? props.integration : true) as SimpleColumnMapping[]
        }, [hasThumbnails, canUpdate, hasManageCostingPermission, hasStockControl, theme, props.companyTaxPercentage, props.descriptionColumnWidth, props.integration])

        useEffect(() => {
            if (mapping.length) {
                props.onColumnMappingLoaded && props.onColumnMappingLoaded(mapping)
            }
        }, [mapping]);

        const controls = useMemo(() => canUpdate ? [
                        {
                            name: 'delete',
                            // type: 'warning',
                            icon: <IconTrash />,
                            label: 'Remove',
                            activeLabel: 'Removing'
                        },
                    ] : [], [canUpdate])

        const sectionControls = useMemo(() => canUpdate ?
                            [
                                ...props.addOptions.map((x: any) => ({
                                    name: x.link,
                                    icon: IconPlus,
                                    label: x.text
                                })).filter((x: any) => x.name !== 'AddBundle'),
                                {
                                    name: 'ungroup'
                                },
                                {
                                    name: 'delete',
                                    icon: IconTrash,
                                    color: 'yellow.7',
                                    label: 'Delete Section'
                                }
                            ] : [], [canUpdate, props.addOptions])

        const stylingProps = useMemo(() => ({
                        compact: true,
                        rows: false,
                        darkerText: true
                    }), [])

        const onAddAction = useStableCallback((name: string) => props.onAddAction && props.onAddAction(name))

        const addButton = useMemo(() => canUpdate ? {
                        // label: 'Add Material / Service',
                        // callback: props.onAddItem,
                        customComponent: <Flex w={'100%'} gap={'md'}>
                            <Menu
                                shadow="md"
                            >
                                <Menu.Target>
                                    <Button c={'scBlue'} size={'compact-sm'} variant={'transparent'} type={'button'} >
                                        <IconPlus size={16} />
                                        <Anchor size={'sm'}>
                                            Add Line
                                        </Anchor>
                                    </Button>
                                </Menu.Target>
                                <Menu.Dropdown>
                                    {
                                        props.addOptions.map(
                                            (x: any, i: any) => (
                                                <Menu.Item key={x.text + i} onClick={() => onAddAction(x.link)}>
                                                    {x.text} {/*{x.newOption && <NewText h={12} />}*/}
                                                </Menu.Item>
                                            )
                                        )
                                    }
                                </Menu.Dropdown>
                            </Menu>
                        </Flex>
                    } : undefined, [canUpdate, props.addOptions, onAddAction])

        return <>
            <Box pos={'relative'}>
                <SectionTable
                    userColumnConfig={props.userColumnConfig}
                    customChildren={props.customChildren}
                    onPredictedDefaultSectionPdfSettingsChanged={props.onPredictedDefaultSectionPdfSettingsChanged}
                    showNewBundleHint
                    data={currentData}
                    savingItem={savingItem}
                    mapping={mapping}
                    controls={controls}
                    sectionControls={sectionControls}
                    onSectionItem={handleSectionAction}
                    onDataUpdate={commit}
                    onAction={handleItemAction}
                    canEdit={canUpdate}
                    stylingProps={stylingProps}
                    onInputChange={updateField}
                    addButton={addButton}
                    height={'100%'}
                    sectionTitleKey={'InventorySectionName'}
                    sectionIdKey={'InventorySectionID'}
                    itemId={props.itemId}
                    module={Enums.Module.Quote}
                    useHideLineItemsPdfSetting
                    useDisplaySubtotalsPdfSetting
                />

            </Box>

            {showImage &&
                <SCModal
                    open={!!showImage}
                    showClose={true}
                    withCloseButton={true}
                    onClose={() => setShowImage(undefined)}
                    size={"auto"}>
                    <ImageWithZoom
                        attachment={{ ContentType: "image/jpeg", Url: showImage.Inventory?.ImageUrl ?? "", UrlThumb: showImage.Inventory?.ThumbnailUrl ?? "" }}
                    />
                </SCModal>
            }

        </>
    }
export default InvoiceSectionTable
